import api, { HalController } from "api-web-client";
import * as Sentry from "@sentry/nextjs";

// eslint-disable-next-line
import { GetServerSideProps } from "next/types";

import { pathnamesKey } from "pathnames";
import { DEFAULT_PRODUCTS_PER_PAGE } from "utils/products";
import { removeEmptyValues } from "utils/object";
import { buildLink } from "utils/buildLink";

function isVisitorLink(
  endpoint: keyof HALVisitor["links"] | (string & {}),
  visitor: HALVisitor
): endpoint is keyof HALVisitor["links"] {
  return endpoint in visitor.links;
}

export function useEndpoint(endpoint: keyof HALVisitor["links"] | (string & {}), idModifier: (id: string) => string) {
  return async function getServerSideProps({ query, resolvedUrl }) {
    const { id, page } = query;
    const locale = query.locale as string;

    const baseUrl = resolvedUrl.replace(`/${locale}`, "").replace(`${query.id}/`, "[id]").replace(/\?.*/, "");
    const routeKey = Object.keys(pathnamesKey).find(
      (pathname) => pathnamesKey[pathname] === baseUrl
    ) as keyof typeof pathnamesKey;

    try {
      const visitor = await api.getVisitor<HALVisitor>(locale);

      let mainResource: any;

      if (isVisitorLink(endpoint, visitor)) {
        mainResource = await visitor.follow(endpoint, { id: idModifier(id as string) });
      } else {
        const apiUrl = new URL(process.env.API_URL);
        apiUrl.pathname = endpoint.replace("{id}", idModifier(id as string));
        apiUrl.searchParams.set("catalog", visitor.data.catalog_id);

        const data = await api.get(apiUrl.pathname + apiUrl.search);

        mainResource = HalController.fromObject(data);
      }

      const resourceId = mainResource.data.slug || mainResource.data.id;

      if (id !== resourceId) {
        return {
          redirect: {
            permanent: true,
            destination: buildLink(routeKey, locale, { id: resourceId }),
          },
        };
      }

      const params = removeEmptyValues({ limit: DEFAULT_PRODUCTS_PER_PAGE, page });
      const productsResource = await mainResource.follow("app:product-list", params);

      return {
        props: {
          _canonical: buildLink(routeKey, locale, removeEmptyValues({ id })),
          _robots: routeKey === "cycle" && mainResource.data.cycle_type === "marketing" ? "noindex" : "",
          routeKey,
          listId: mainResource.data.id,
          listName: mainResource.data.name || "",
          products: productsResource.toObject(),
          namespaceRequired: ["common"],
        },
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return { notFound: true };
      }

      Sentry.captureException(error);
      throw error;
    }
  } as GetServerSideProps;
}
